import React from "react";
import { useDispatch } from "react-redux";
import { func, string, object, number } from "prop-types";
import { useTranslation } from "@lib/i18n";

import StickyRequestButton from "@components/shared/StickyRequestButton";
import Typography from "../../shared/Typography";
import DeliveryOption from "../DeliveryOption";
import ErrorMessage from "@components/shared/ErrorMessage";
import DeliveryOptionPrice from "components/Delivery/steps/shared/DeliveryOptionPrice";
import gtm from "@gtm/core";
import api from "api";

import { priceAlgorithm } from "@lib/priceAlgorithm";
import { changeDeliveryTypeEvent } from "@gtm/events/shared";
import { useIsLLDJourneySelected } from "@hooks/useIsLLDJourneySelected";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import { useDeliveryMethods } from "@hooks/useDeliveryMethods";
import { useEffect } from "react";
import { removeBasketErrorAction } from "@redux/actions/errorsActions";
import { useIsLLDPriceBreakdownRequestPendingSelector } from "@redux/reducers/requests";

import { REQUEST_ERROR_CODES, DELIVERY_METHOD } from "@shared/constants";

import { useTaxPercentageSelector } from "@redux/reducers/appConfig";

import {
  useUserProfileSelector,
  useCarConfigurationSelector,
  useFinanceSimulationSelector,
  useBusinessModelSelector,
  useFinanceDeliveryPricesSelector,
} from "@redux/reducers/deal";

import {
  setDeliveryDataAction,
  updateDeliveryPriceAction,
  setDeliveryDealerAction,
} from "@redux/actions/dealActions";

import {
  basketDeliveryEcommerceEvent,
  basketDeliveryContinueEcommerceEvent,
} from "@gtm/events/basket";

const TypeSelection = ({
  onNextState,
  setIsHomeDelivery,
  primaryScreenErrorMessage,
  salesmanPoint,
  setDeliveryAddress,
  fixedHeaderHeight,
}) => {
  const taxPercentage = useTaxPercentageSelector();
  const businessModel = useBusinessModelSelector();
  const deliveryMethods = useDeliveryMethods(businessModel);
  const enabledDeliveryMethods = deliveryMethods.filter(
    ({ enabled }) => enabled
  );

  const { deliveryData } = useUserProfileSelector();
  const financeDeliveryPrices = useFinanceDeliveryPricesSelector();
  const isSingleDeliveryMethod = enabledDeliveryMethods.length === 1;

  const selectedDeliveryIndex = enabledDeliveryMethods.findIndex(
    ({ type }) => type === deliveryData?.type
  );

  const updateSelectedDelivery = index => {
    const selectedDelivery = enabledDeliveryMethods[index];

    dispatch(setDeliveryDataAction(selectedDelivery));
  };

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedCarConfiguration = useCarConfigurationSelector();
  const { journey, totalPrice } = useFinanceSimulationSelector();
  const isLLDJourneySelected = useIsLLDJourneySelected();
  const isLLDPriceBreakdownRequestPending = useIsLLDPriceBreakdownRequestPendingSelector();
  const isB2BSelected = useIsB2BSelected();
  const selectedDelivery =
    enabledDeliveryMethods[selectedDeliveryIndex] ?? null;
  const deliveryType = selectedDelivery?.type;
  const isHomeDelivery = deliveryType === DELIVERY_METHOD.HOME;
  const isSalesmanPoint = deliveryType === DELIVERY_METHOD.SALESMAN_POINT;

  const onContinue = () => {
    if (isSalesmanPoint) {
      setDeliveryAddress(salesmanPoint);
      dispatch(setDeliveryDealerAction(salesmanPoint));
    }

    setIsHomeDelivery(isHomeDelivery);
    // setState is async, change might not take effect
    onNextState({
      isHomeDelivery,
      isSalesmanPoint,
    });

    dispatch(setDeliveryDataAction(selectedDelivery));
    dispatch(
      removeBasketErrorAction(REQUEST_ERROR_CODES.DELIVERY_METHOD_UNAVAILABLE)
    );
    // if a customer selects home delivery, call finance gateway to update the monthly price
    gtm.fire(
      basketDeliveryContinueEcommerceEvent({ deliveryType, isB2BSelected })
    );
  };

  useEffect(() => {
    gtm.fire(
      basketDeliveryEcommerceEvent({
        price: totalPrice.cash.netPrice,
        variant: selectedCarConfiguration.preconfiguration.label,
        isB2BSelected,
      })
    );
  }, []);

  useEffect(() => {
    if (selectedDeliveryIndex === -1) {
      return;
    }

    const { netPrice } = enabledDeliveryMethods[selectedDeliveryIndex];

    dispatch(
      updateDeliveryPriceAction(
        priceAlgorithm.getPrice(netPrice, taxPercentage)
      )
    );
  }, [selectedDeliveryIndex, journey]);

  // This automatically selects the only delivery option available
  // The side-effect is that if you hit F5 with validation error of delivery method
  // being no longer available, you will loose that error after this effect runs
  // In other words: By automatically selecting the only available delivery method
  // we are getting updated prices, but we are also are loosing the validation errors from
  // whatever was selected before the automatic selection of the new value
  useEffect(() => {
    if (isSingleDeliveryMethod) {
      const { label } = enabledDeliveryMethods[0];

      updateSelectedDelivery(0);
      gtm.fire(changeDeliveryTypeEvent(label));
    }
  }, []);

  const onRequestButtonClick = () => {
    const selectedDelivery = enabledDeliveryMethods[selectedDeliveryIndex];

    return api.updateSession({
      delivery: {
        type: selectedDelivery.id,
        point: isSalesmanPoint ? salesmanPoint.id : null,
      },
      // store financeDeliveryPrices in session
      financeDeliveryPrices: financeDeliveryPrices,
    });
  };

  return (
    <>
      {!isSingleDeliveryMethod && (
        <Typography className="text-sm uppercase">
          {t("basket.delivery.description")}
        </Typography>
      )}
      <fieldset className="py-4 mt-4 flex flex-col gap-4">
        <legend className="text-black font-semibold uppercase">
          {t("basket.delivery.selectTitle")}
        </legend>
        {enabledDeliveryMethods.map((method, index) => {
          if (
            method.type === DELIVERY_METHOD.SALESMAN_POINT &&
            !salesmanPoint
          ) {
            return null;
          }

          const {
            label,
            shortDescription,
            netPrice,
            longDescription,
            id,
          } = method;
          const selected =
            isSingleDeliveryMethod || selectedDeliveryIndex === index;

          return (
            <DeliveryOption
              key={index}
              selected={selected}
              label={
                salesmanPoint
                  ? label.replace(`{{label}}`, salesmanPoint.name)
                  : label
              }
              description={shortDescription}
              formattedPrice={
                <DeliveryOptionPrice
                  deliveryId={id}
                  netPrice={Number(netPrice)}
                />
              }
              moreInformation={longDescription}
              onChange={() => {
                updateSelectedDelivery(index);
                gtm.fire(changeDeliveryTypeEvent(label));
              }}
              identifier={index}
              hideInput={isSingleDeliveryMethod}
              salesmanPoint={salesmanPoint}
            />
          );
        })}
      </fieldset>
      <div className="flex flex-col justify-center items-center w-full pt-4 pb-12">
        {primaryScreenErrorMessage && (
          <div className="mb-8">
            <ErrorMessage>{primaryScreenErrorMessage}</ErrorMessage>
          </div>
        )}
        <StickyRequestButton
          data-id="delivery-type-continue-button"
          disabled={selectedDeliveryIndex === -1}
          fixedHeaderHeight={fixedHeaderHeight}
          isLoading={!isLLDJourneySelected && isLLDPriceBreakdownRequestPending}
          isStickyToBottom={true}
          isStickyToTop={true}
          onRequestError={console.error}
          onRequestSuccess={onContinue}
          triggerRequestCallback={onRequestButtonClick}
        >
          {t("basket.delivery.continue")}
        </StickyRequestButton>
      </div>
    </>
  );
};

TypeSelection.propTypes = {
  onNextState: func,
  setIsHomeDelivery: func,
  primaryScreenErrorMessage: string,
  salesmanPoint: object,
  setDeliveryAddress: func,
  fixedHeaderHeight: number,
};

export default TypeSelection;
